import { useRef, Children } from "react";
import { Link } from "react-router-dom";

// Horizontal carousel: shows 4 cards per view, pages with prev/next buttons.
const ProductRail = ({ children }) => {
  const ref = useRef(null);
  const scroll = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  };
  const showNav = Children.count(children) > 4;
  return (
    <div className="ds-rail">
      {showNav && (
        <button type="button" className="ds-rail-btn prev" onClick={() => scroll(-1)} aria-label="Previous">‹</button>
      )}
      <div className="ds-rail-track" ref={ref}>
        {children}
      </div>
      {showNav && (
        <button type="button" className="ds-rail-btn next" onClick={() => scroll(1)} aria-label="Next">›</button>
      )}
    </div>
  );
};

const HomePageTemplate = ({ templateData, templateEvent }) => {
  const { categories, products } = templateData;
  const { handleSearch, handleProductClick } = templateEvent;

  return (
    <main>
      {/* Hero */}
      <section className="ds-container" style={{ paddingTop: 24 }}>
        <div className="ds-hero">
          <div className="ds-hero-inner">
            <p className="ds-eyebrow" style={{ color: "var(--brand-teal)" }}>
              The building blocks for engineers start here
            </p>
            <h1>
              Every circuit starts with the<br />right components<span className="dot">.</span>
            </h1>
            <p>
              Search 500M+ electronic components across trusted distributors,
              compare live pricing and stock, and turn a BOM into a costed order
              in minutes.
            </p>
            <div className="ds-row">
              <a href="#products" className="ds-btn ds-btn-primary ds-btn-lg">Shop now</a>
            </div>
          </div>
          <img className="glyph" src="/assets/img/logo-360-mark.svg" alt="" />
        </div>
      </section>

      {/* New products per group */}
      <div id="products">
        {products?.map((product, productIndex) => {
          const sortedDetails = [...(product.details || [])]
            .sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
            .slice(0, 12);
          if (sortedDetails.length === 0) return null;

          return (
            <section className="ds-container ds-section-sm" key={product.id || productIndex}>
              <div className="ds-head-row ds-mb-l">
                <div>
                  <p className="ds-eyebrow">Fresh stock</p>
                  <h2>{product.name}</h2>
                </div>
                <a
                  className="ds-link"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleProductClick(product.slug)}
                >
                  View all products →
                </a>
              </div>
              <ProductRail>
                {sortedDetails.map((detail) => {
                  const pn = detail?.inventory?.part_number || product.name;
                  const img = detail?.inventory?.image || product.image;
                  return (
                    <Link className="ds-product-card" to={`/product/${pn}`} key={detail.id}>
                      <span className="ds-thumb">
                        {img ? <img src={img} alt={pn} /> : null}
                      </span>
                      <span className="ds-product-body">
                        <span className="ds-mpn">{pn}</span>
                        <span className="ds-maker">{product.name}</span>
                      </span>
                    </Link>
                  );
                })}
              </ProductRail>
            </section>
          );
        })}
      </div>

      {/* Popular categories */}
      <section className="ds-container ds-section-sm">
        <div className="ds-head-row ds-mb-l">
          <div>
            <p className="ds-eyebrow">Browse</p>
            <h2>Popular categories</h2>
          </div>
          <Link to="/category-list" className="ds-link">All categories →</Link>
        </div>
        <div className="ds-card-grid cols-4">
          {categories?.length > 0 ? (
            categories.map((category, index) => (
              <Link
                to={`/inventory/${category.unique_id}`}
                className="ds-category-card"
                key={category.id || index}
                style={{ display: "flex", alignItems: "center", gap: 14 }}
              >
                <img
                  src={category.image || "/assets/img/cat-img1.png"}
                  alt={category.name}
                  style={{ width: 56, height: 40, objectFit: "contain", flex: "0 0 auto" }}
                />
                <h4 style={{ margin: 0 }}>{category.name}</h4>
              </Link>
            ))
          ) : (
            <p className="ds-muted">No categories found</p>
          )}
        </div>
      </section>

      {/* Featured manufacturers */}
      <section className="ds-container ds-section-sm" style={{ paddingBottom: 56 }}>
        <div className="text-center ds-mb-l" style={{ textAlign: "center" }}>
          <p className="ds-eyebrow">Trusted by engineers</p>
          <h2>Featured manufacturers</h2>
        </div>
        <div className="ds-logo-strip">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <img key={n} src={`/assets/img/client-logo${n}.png`} alt="Manufacturer" style={{ maxHeight: 46, opacity: 0.8 }} />
          ))}
        </div>
      </section>
    </main>
  );
};

export default HomePageTemplate;
